import { ITEM_TAX_OPTIONS } from "../../../../../utils/Constants";

// The two name fields are per-language columns, not UI-locale dependent: item_name_ja
// is always Japanese no matter which locale the operator is browsing in. So the
// suffixes are constants per field rather than i18n lookups.
export const CLONE_NAME_SUFFIX = "(clone)";
export const CLONE_NAME_SUFFIX_JA = "（コピー）";

interface MenuItemAddon {
  option_id?: string;
  item_id?: string;
}

interface AddonOption {
  assigned_items?: { _id?: string }[];
}

export interface MenuItemDetail {
  /** Present on the detail response but deliberately not carried onto the clone. */
  _id?: string;
  item_code?: string;
  image?: string;
  item_name?: string;
  item_name_ja?: string;
  category_id?: string;
  sub_category_id?: string;
  short_description?: string;
  short_description_ja?: string;
  addons?: MenuItemAddon[];
  price?: number;
  total_price?: number;
  cost_price?: number;
  consumption_tax_rate?: number;
  item_tax_type?: string;
  spice_level_needed?: boolean;
  is_upsell?: boolean;
  type?: string;
  item_variation?: string;
  upsell_items?: string[];
}

interface BuildClonedMenuItemValuesDeps {
  /** Resolves an addon option by id so stale addon items can be pruned. */
  getAddon: (optionId?: string) => AddonOption | undefined;
  /** Drops upsell ids that are no longer selectable. */
  sanitizeUpsellSelection: (ids: string[]) => string[];
}

/**
 * Maps a menu item detail response onto the ADD drawer's form values, suffixing
 * each name field with a clone marker in that field's own language.
 *
 * Three fields deliberately do not carry over:
 * - `item_code` is blanked so the caller can re-issue a fresh one.
 * - `image` is blanked because the add form's image field holds a File; a URL
 *   string breaks createObjectURL.
 * - addon `item_id` is blanked when that item is no longer assigned to the option.
 *
 * Unlike the edit form, upsell ids are sanitized without an exclusion, so a
 * clone may list its own source item as an upsell — the clone has no id yet.
 */
export const buildClonedMenuItemValues = (
  detail: MenuItemDetail | null | undefined,
  { getAddon, sanitizeUpsellSelection }: BuildClonedMenuItemValuesDeps
) => {
  const source = detail || {};

  const activeAddons = (source?.addons || []).map((addon) => {
    const assignedItems = getAddon(addon?.option_id)?.assigned_items || [];
    const itemExists = assignedItems.some((addonItem) => addonItem?._id === addon?.item_id);
    return {
      item_id: itemExists ? addon.item_id : "",
      option_id: addon?.option_id,
    };
  });

  const isInclusive = source?.item_tax_type === ITEM_TAX_OPTIONS.INCLUSIVE;

  return {
    item_name: source?.item_name ? `${source.item_name}${CLONE_NAME_SUFFIX}` : "",
    item_name_ja: source?.item_name_ja ? `${source.item_name_ja}${CLONE_NAME_SUFFIX_JA}` : "",
    category_id: source?.category_id || "",
    sub_category_id: source?.sub_category_id || "",
    item_code: "",
    short_description: source?.short_description || "",
    short_description_ja: source?.short_description_ja || "",
    selected_addon: "",
    addons: activeAddons,
    price: source?.total_price && isInclusive ? source.total_price : source?.price || 0,
    cost_price: source?.cost_price || 0,
    consumption_tax_rate: source?.consumption_tax_rate || 0,
    item_tax_type: source?.item_tax_type || ITEM_TAX_OPTIONS.INCLUSIVE,
    spice_level_needed: source?.spice_level_needed || false,
    is_upsell: source?.is_upsell || false,
    image: "",
    type: source?.type || "",
    item_variation: source?.item_variation || "",
    upsell_items: sanitizeUpsellSelection(source?.upsell_items || []),
  };
};
